import React, { forwardRef, useMemo, useCallback } from 'react'
import withWindowScreenWorkflowConfigs from '../../Designer/withWindowScreenWorkflowConfigs.js'
import DeleteWorkflowModal from '../../Designer/DeleteWorkflowModal.jsx'
import ConfigureStepModal from '../../Designer/ConfigureStepModal.jsx'
import EditWorkflowModal from '../../Designer/EditWorkflowModal.jsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getCurrentScreenType } from 'Helper/screenTypeHelpers'
import { openModal, closeModal } from 'actions/modalActions'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { push } from 'Helper/routeHelpers'
import { compose } from 'redux'
import _ from 'lodash'

const WorkflowDisplay = forwardRef(({
  workflow,
  workflowStep,
  stepChangeHandler,
  cancelWorkflow,
  contextWorkflowConfigs,
  contextWorkflowConfigsId
}, ref) => {
  const dispatch = useDispatch()
  const screenType = getCurrentScreenType() === 'ipad' ? 'IPad' : 'Standard'
  const designMode = useSelector(state => state.interactableState.uiDesignMode)
  const { pathname } = useLocation()

  const currentWorkflowPath = useSelector(state => state.router?.match.params.workflow)

  const merge = useCallback((a, b) => {
    const c = []
    let index = 0
    for (let i = 0; i < a.length; i++) {
      c[index] = a[i]
      if (i < b.length) {
        index++
        c[index] = b[i]
      }
      index++
    }
    return c
  }, [])

  const workflowButtons = workflow.sequence.map((step, idx) => {
    return (
      <WorkflowButton
        name={step.name}
        index={idx}
        workflowStep={workflowStep}
        key={idx}
        stepChangeHandler={designMode ? () => openConfigureStepModal(true, idx) : stepChangeHandler}
        designMode={designMode}
      />
    )
  })

  const arrows = useMemo(() => workflow.sequence.map((item, idx) => {
    return (
      <FontAwesomeIcon
        key={'arrow' + idx}
        icon={['fas', 'long-arrow-alt-right']}
        size="2x"
        className="arrow"
      />
    )
  }), [workflow])

  const openConfigureStepModal = useCallback((isEdit, index) => {
    const split = pathname.split('/step')
    const stepNum = index + 1
    if(stepNum > 0 && workflow?.sequence && workflow.sequence[index]) {
      // if we are opening a step that exists, navigate to it
      push(`${split[0]}/step/${stepNum}`)
    }
    dispatch(openModal(
      'configure_step_modal',
      {
        title: isEdit ? 'Edit Step' : 'Add Step',
        content: (
          <ConfigureStepModal
            selectedStepIndex={index}
            workflowConfig={workflow}
            isEdit={isEdit}
            closeSelf={() => dispatch(closeModal('configure_step_modal'))}
          />
        ),
        closeable: true
      })
    )
  }, [workflow, pathname])

  const editWorkflow = useCallback(() => {
    dispatch(openModal(
      'edit_workflow_modal',
      {
        title: 'Configure ' + screenType + ' Workflow',
        content: (
          <EditWorkflowModal
            isEdit={true}
            closeSelf={() => { dispatch(closeModal('edit_workflow_modal')) }}
          />
        ),
        closeable: true
      })
    )
  })

  const confirmDeleteModal = useCallback(() => {
    const closeSelf = () => dispatch(closeModal('confirm_modal'))
    dispatch(openModal('confirm_modal', {
      title: 'Delete Workflow',
      content: (
        <DeleteWorkflowModal closeSelf={closeSelf} workflows={contextWorkflowConfigs} workflowId={contextWorkflowConfigsId} currentWorkflowPath={currentWorkflowPath} workflowName={workflow?.name}/>
      ),
      closeable: true
    }))
  }, [contextWorkflowConfigs, contextWorkflowConfigsId, currentWorkflowPath])

  const hasFinalStep =
    workflow.sequence &&
    workflow.sequence.length > 0 &&
    workflow.sequence[workflow.sequence.length - 1].url != null

  return (
    <div className="workflow-wrapper" ref={ref}>
      {`${workflow.name} workflow in progress...`}
      <div>
        <div className="workflow-menu">
          {merge(workflowButtons, arrows).slice(
            0,
            workflow.sequence.length * 2 - 1
          )}
          {
            designMode
              ? <button
                  className='btn btn-success add-btn'
                  aria-label="add step button"
                  data-toggle={!hasFinalStep ? null : 'tooltip'}
                  data-title={
                    'The previously added step terminates the current workflow by sending the user to another workflow or screen context. No additional steps may be added.'
                  }
                  data-placement="top"
                  onClick={() => { openConfigureStepModal(false, workflow.sequence.length - 1)}}
                  disabled={hasFinalStep}
                >
                <FontAwesomeIcon
                  size="1x"
                  key={'add'}
                  icon={['fas', 'plus']}
                /> Add
              </button>
              : null
          }
        </div>
        <div className="workflow-btns">
          {
            designMode
              ? <div>
                <button
                  className="btn btn-success"
                  onClick={editWorkflow}
                  aria-label="edit workflow button"
                >
                  Edit
                </button>
                <button
                  className="btn btn-warning"
                  onClick={confirmDeleteModal}
                  aria-label="delete workflow button"
                >
                  Delete
                </button>
              </div>
              : null
          }
          <button
            className="btn btn-danger"
            onClick={cancelWorkflow}
            aria-label="cancel workflow button">
            Exit Workflow
          </button>
        </div>
      </div>
    </div>
  )
})

const enhance = compose(
  withWindowScreenWorkflowConfigs()
)

export default enhance(WorkflowDisplay)

const WorkflowButton = ({ index, workflowStep, stepChangeHandler, name, designMode }) => {
  const stepChange = () => {
    if (designMode || (!designMode && index <= workflowStep + 1)) {
      stepChangeHandler(index)
    }
  }

  let icon
  let stepClass
  if (designMode) {
    if(index === workflowStep) {
      icon = ['far', 'pen-to-square']
      stepClass = 'current-step'
    } else {
      icon = ['far', 'pen-to-square']
      stepClass = 'design-step'
    }
  } else {
    if (index === workflowStep) {
      icon = ['fas', 'play-circle']
      stepClass = 'current-step'
    } else if (index > workflowStep + 1) {
      // disable workflow buttons beyond 1 step ahead
      icon = ['far', 'times-circle']
      stepClass = 'future-step'
    } else if (index === workflowStep + 1) {
      icon = ['far', 'circle']
      stepClass = 'next-step'
    } else {
      icon = ['fas', 'check-circle']
      stepClass = 'past-step'
    }
  }

  return (
    <div
      className='step'
      key={'stepButton' + index}
      aria-label={`${name} workflow step button`}
      onClick={stepChange}
    >
      <FontAwesomeIcon icon={icon} size="2x" className={stepClass} />
      <FontAwesomeIcon icon={['fas', 'play-circle']} size="2x" className={'current-step'} style={{ display: 'none' }} />
      {name}
    </div>
  )
}
